import React from 'react';
import ViewReverse from './ViewReverse';
import { ReactSVG } from 'react-svg';
import Text from './Text';
import { fontSizes } from '../constants/constants';
import dynamicfunction from '../utils/dynamicfunction';
import icons from '../constants/icons';
import { useSelector } from 'react-redux';
import { cn, getContentKeyAccToLang } from '../utils/utils';
import { sidebarMenu } from '../config/config';
import { useLocation, useNavigate } from 'react-router';
import { clientSuspendedToast } from '../utils/functions';

const Sidebar = ({ children }) => {
  const pathname = useLocation();
  const navigate = useNavigate();
  const { TextWeight, isArabic } = dynamicfunction();

  const { user } = useSelector((state) => state.common);
  const { dashboardContent } = useSelector((state) => state.content);
  const { clientDetails } = useSelector((state) => state.investments);

  const getContentKey = (key) => {
    return getContentKeyAccToLang(dashboardContent, key, isArabic);
  };

  const sections = sidebarMenu(pathname?.pathname);

  const [expandedKey, setExpandedKey] = React.useState(
    () => sections?.find((section) => section?.isActive)?.key ?? null
  );

  React.useEffect(() => {
    const activeKey = sections?.find((section) => section?.isActive)?.key;
    if (activeKey) setExpandedKey(activeKey);
  }, [pathname?.pathname]);

  const onPressMenu = (route) => navigate(route);

  const onSectionClick = (section, is_disabled) => {
    if (is_disabled) return;
    if (section?.childs?.length > 0) {
      setExpandedKey((prev) => (prev === section?.key ? null : section?.key));
      return;
    }
    const is_suspended = section?.key === 'cash_management' && user?.isClientSuspended;
    onPressMenu(is_suspended ? section?.suspended_route : section?.route);
  };

  const onChildClick = (child, is_disabled) => {
    if (is_disabled) return;
    const is_suspend = child?.suspendedRoute && user?.isClientSuspended;
    if (is_suspend) {
      clientSuspendedToast(isArabic);
    } else {
      onPressMenu(child?.route);
    }
  };

  return (
    <div className={'grid grid-cols-12 flex-1'}>
      <div
        className={cn(
          isArabic ? 'order-2' : 'order-1',
          user ? 'hidden lg:block lg:col-span-3' : 'hidden'
        )}
      >
        {sections?.map((section, index) => {
          const is_disabled = user ? false : index !== 0;
          return (
            <div
              key={section?.key ?? index}
              className={cn('transition-all duration-300 ease-in-out', is_disabled ? 'opacity-50 cursor-not-allowed' : '', index !== sections.length - 1 && expandedKey !== section?.key ? 'border-b border-b-grey' : '')}
            >
              <ViewReverse
                className={'items-center justify-start gap-3 px-2 sm:px-4 lg:px-6 py-4 lg:py-9 bg-sidebar-grey cursor-pointer'}
                onClick={() => onSectionClick(section, is_disabled)}
              >
                <ViewReverse className={'gap-2 sm:gap-3 items-center'}>
                  <ReactSVG
                    src={section?.isActive ? section?.activeIcon : section?.disableIcon}
                    className='scale-75 md:scale-100'
                  />
                  <Text
                    text={getContentKey(section?.key)}
                    size={fontSizes.h16}
                    isTruncate={false}
                    weight={TextWeight.medium}
                    color={`${section?.isActive ? 'text-primary' : 'text-dark-grey'}`}
                    className={'hidden lg:block'}
                  />
                </ViewReverse>
                {section?.childs?.length > 0 && (
                  <ReactSVG
                    src={icons.upArrowIcon}
                    className={cn('hidden sm:block', expandedKey === section?.key ? '' : '-rotate-180')}
                  />
                )}
              </ViewReverse>

              {expandedKey === section?.key && section?.childs?.length > 0 && (
                <div className={'bg-white flex flex-col py-3'}>
                  {section?.childs?.map((child) => {
                    const is_child_disabled = is_disabled || (child?.withdrawalRoute && !clientDetails?.enableWithdrawal);
                    return (
                      <ViewReverse
                        key={child?.key}
                        className={cn('gap-2 sm:gap-3 items-center px-2 sm:px-4 lg:px-6 py-2.5 lg:py-3', is_child_disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
                        onClick={() => onChildClick(child, is_child_disabled)}
                      >
                        <ReactSVG
                          src={child?.isActive ? child?.activeIcon : child?.disableIcon}
                          className='scale-75 md:scale-100'
                        />
                        <Text
                          text={getContentKey(child?.key)}
                          size={fontSizes.h16}
                          weight={TextWeight.medium}
                          color={child?.isActive ? 'text-primary' : 'text-dark-grey'}
                          className={'hidden lg:block'}
                          isTruncate={false}
                        />
                      </ViewReverse>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div
        className={cn(
          'h-full bg-white p-4 md:p-8',
          isArabic ? 'order-1 lg:ps-20' : 'order-2 lg:pe-20',
          user ? 'col-span-12 lg:col-span-9' : 'col-span-12 lg:px-20'
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default Sidebar;
